import { ChangeEvent, FunctionComponent, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { confirmPopup } from "primereact/confirmpopup";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";

import { faFileArrowDown, faMessagePlus, faPaperPlaneTop } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AllowedGroundingFiles } from "../../../../../@api";

import { formatSupportedChatModelName } from "../../../../../utils/text-utils";
import { ActionButton } from "../../../../components/Shared";
import { TooltipWrapper } from "../../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useAiModelsService, useChatService } from "../../../../services/ServiceHooks";
import { useStore } from "../../../../stores/StoreContext";
import { useIsChatDisabled } from "../../Hooks/useIsChatDisabled";
import { Disclaimer } from "../Disclaimer/Disclaimer";
import { FileAttachments } from "../FileAttachments/FileAttachments";
import { useFileDropZone } from "../FileDropZone/useFileDropZone";
import { FileSelector } from "../FileSelector/FileSelector";

import "../FileDropZone/FileDropZone.scss";
import "./ChatPrompt.scss";

const RawChatPrompt: FunctionComponent = () => {
  const { t } = useTranslation();
  const { threadStore, budgetStore, settingsStore, chatStore } = useStore();
  const chatbotService = useChatService();

  const isChatDisabled = useIsChatDisabled();
  const aiModelsService = useAiModelsService();

  const [inputComponentTooltipKey, setInputComponentTooltipKey] = useState<string | undefined>();
  const [hasActiveThreadExchanges, setHasActiveThreadExchanges] = useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const [supportedChatModelName, setSupportedChatModelName] = useState<string>("");
  const [value, setValue] = useState("");
  const [allowedFileGroundings, setAllowedFileGroundings] = useState<AllowedGroundingFiles | undefined>();

  const inputPromptWrapperRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const invalidStringRef = useRef("p-invalid");
  const startNewThreadButtonRef = useRef<HTMLDivElement>(null);

  const featureTranslationKeys = translationKeys.feature.chatbot.tooltips;

  const clearInvalidIndicator = useCallback(() => {
    const textArea = document.getElementById("chat-textarea");
    textArea?.classList.remove(invalidStringRef.current);
  }, []);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
    if (event.target.value.length > 0) {
      clearInvalidIndicator();
    }
  }, []);

  const handleSubmitQuestion = async () => {
    const requestThread = threadStore.activeThread;
    const chatInput = value.trim();
    if (!!requestThread?.pendingPrompt || isChatDisabled || chatInput.length === 0) {
      return;
    }

    setValue("");

    const filesToSend = chatStore.selectedFiles;

    await chatbotService.sendChatMessage(requestThread, chatInput, filesToSend);
    chatStore.clearSelectedFiles();
  };

  const handleEnterPressed = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        return;
      }
      event.preventDefault();
      handleSubmitQuestion();
    }
  };

  const cancelStartNewThread = () => {};

  const startNewThread = () => {
    threadStore.resetActiveThread();
  };

  const confirmStartNewThread = () => {
    // there is no need to confirm if the user has enabled chat persistence,
    // as the thread will be saved and can be resumed later
    if (settingsStore.persistChat) {
      startNewThread();
      return;
    }

    confirmPopup({
      target: startNewThreadButtonRef.current ?? undefined,
      message: t(translationKeys.confirm.startNewThread),
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      style: { marginTop: "0.9rem" },
      reject: cancelStartNewThread,
      accept: startNewThread,
      rejectLabel: t(translationKeys.actions.cancel),
      acceptIcon: <FontAwesomeIcon icon={faMessagePlus} />,
      acceptLabel: t(translationKeys.actions.startNewThread),
      acceptClassName: "inline-flex align-items-center gap-2 icon-button-reverse",
    });
  };

  useEffect(() => {
    if (threadStore.pendingCopyResponseToInput?.length) {
      setValue(threadStore.pendingCopyResponseToInput);

      focusTextArea();

      // Reset the pending copy response to input
      threadStore.triggerCopyResponseToInput(undefined);
    }
  }, [threadStore.pendingCopyResponseToInput]);

  const focusTextArea = () => {
    setTimeout(() => {
      textAreaRef.current?.focus();
      textAreaRef.current?.click();
    }, 100);
  };

  useEffect(() => {
    if (budgetStore.hasBudget === false) {
      setInputComponentTooltipKey(translationKeys.globalErrors.serviceUnavailable);
    }
  }, [budgetStore.hasBudget]);

  useEffect(() => {
    if (!isChatDisabled && textAreaRef?.current) {
      focusTextArea();
    }
  }, [isChatDisabled]);

  useEffect(() => {
    const exchanges = threadStore.activeThread?.exchanges;
    setHasActiveThreadExchanges(!!exchanges?.length);
  }, [threadStore.activeThread?.exchanges?.length]);

  useEffect(() => {
    let isMounted = true;

    const loadSupportedChatModel = async () => {
      try {
        const response = await aiModelsService.getSupportedChatModel();
        if (isMounted) {
          setSupportedChatModelName(response.modelName);
        }
      } catch (error) {
        if (isMounted) {
          setSupportedChatModelName("");
          console.debug("Failed to load supported chat model", error);
        }
      }
    };

    const loadAllowedGroundingFiles = async () => {
      try {
        const response = await chatbotService.getAllowedGroundingFiles();
        setAllowedFileGroundings(response);
      } catch (error) {
        console.debug("Failed to load allowed grounding files", error);
      }
    };

    loadSupportedChatModel();
    loadAllowedGroundingFiles();

    return () => {
      isMounted = false;
    };
  }, []);

  const { dropZoneProps, isDraggingOver, isDropAnimating, errorToastRef, overlayLabel } = useFileDropZone({
    allowedFileTypes: allowedFileGroundings?.allowedFileExtensions ?? [],
    allowedFileSize: allowedFileGroundings?.maxSizeInBytesPerFile ?? 0,
    allowedTotalFileSize: allowedFileGroundings?.totalSizeInBytes ?? 0,
    disabled: isChatDisabled || !allowedFileGroundings,
  });

  const formattedSupportedChatModelName = formatSupportedChatModelName(supportedChatModelName);

  const sendButtonTooltip = (): string => {
    if (!budgetStore.hasBudget) {
      return t(translationKeys.globalErrors.budgetErrors.outOfBudget);
    }

    if (!value?.length) {
      return t(featureTranslationKeys.sendPromptButtonDisabledNoQuestion);
    }

    if (threadStore.activeThread?.pendingPrompt) {
      return t(featureTranslationKeys.sendPromptButtonDisabledPendingExchange);
    }

    return t(featureTranslationKeys.sendMessage);
  };

  return (
    <div className="input-component">
      <TooltipWrapper
        targetRef={inputPromptWrapperRef}
        position="top"
        disabled={!isChatDisabled || !inputComponentTooltipKey}
        content={inputComponentTooltipKey && t(inputComponentTooltipKey)}
      />

      <div
        className={classNames("input-component-form", "file-drop-zone", {
          disabled: isChatDisabled,
          focus: isInputFocused,
          "is-dragging-over": isDraggingOver,
          "is-drop-animating": isDropAnimating,
        })}
        ref={inputPromptWrapperRef}
        {...dropZoneProps}
      >
        <Toast ref={errorToastRef} position="top-center" />

        <FileAttachments />

        <InputTextarea
          id="chatTextarea"
          className="chat-textarea"
          ref={textAreaRef}
          value={value}
          onKeyDown={handleEnterPressed}
          onChange={handleInputChange}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          rows={1}
          disabled={isChatDisabled}
          autoResize={true}
          placeholder={t(translationKeys.inputRow.inputPlaceholder)}
        />

        <div className="input-footer">
          <div className="left-actions">
            <FileSelector
              allowedFileSize={allowedFileGroundings?.maxSizeInBytesPerFile ?? 0}
              allowedTotalFileSize={allowedFileGroundings?.totalSizeInBytes ?? 0}
              allowedFileTypes={allowedFileGroundings?.allowedFileExtensions ?? []}
              disabled={!allowedFileGroundings}
            />

            <Disclaimer
              type="popup"
              fallbackText={t(translationKeys.feature.chatbot.webSearch.disclaimer.contentMarkdown)}
              doNotShowAgainKey="doNotShowAgainDisclaimer"
              focusTextArea={focusTextArea}
            />

            {!!hasActiveThreadExchanges && (
              <div ref={startNewThreadButtonRef}>
                <ActionButton
                  id="startNewThreadButton"
                  icon={faMessagePlus}
                  hideBackground={true}
                  hideBackgroundHover={true}
                  buttonDisabled={isChatDisabled}
                  tooltipDisabled={false}
                  tooltip={t(featureTranslationKeys.startNewThread)}
                  tooltipPosition="top"
                  onClick={confirmStartNewThread}
                />
              </div>
            )}
          </div>

          <div className="right-actions">
            <div className="model-label">
              {formattedSupportedChatModelName
                ? t(translationKeys.feature.chatbot.modelLabel, {
                    modelName: formattedSupportedChatModelName,
                    defaultValue: formattedSupportedChatModelName,
                  })
                : ""}
            </div>

            <ActionButton
              id="chatbotSendPromptButton"
              icon={faPaperPlaneTop}
              hideBackground={true}
              hideBackgroundHover={true}
              buttonDisabled={!value?.length || !!threadStore.activeThread?.pendingPrompt || isChatDisabled}
              tooltipDisabled={false}
              tooltip={sendButtonTooltip()}
              tooltipPosition="top"
              onClick={handleSubmitQuestion}
            />
          </div>
        </div>

        {isDraggingOver && (
          <div className="file-drop-zone-overlay">
            <FontAwesomeIcon icon={faFileArrowDown} size="2x" />
            <span>{overlayLabel}</span>
          </div>
        )}

        <TooltipWrapper
          targetRef=".has-pending-exchange-tooltip"
          position="top"
          disabled={!threadStore.activeThread?.pendingPrompt || isChatDisabled}
          showOnDisabled={true}
          content={t(featureTranslationKeys.sendPromptButtonDisabledPendingExchange)}
        />
      </div>
    </div>
  );
};

export const ChatPrompt = observer(RawChatPrompt);
